import { createSlice } from "@reduxjs/toolkit";
import { bloqcibeApi } from "./apiSlice";
import jwt_decode from "jwt-decode";

let savedToken = localStorage.getItem("token");
if (savedToken) {
  savedToken = JSON.parse(savedToken);
}

// Load persisted getMe data from localStorage (separate from token)
let persistedUserData = null;
try {
  const savedUserData = localStorage.getItem("userData");
  if (savedUserData) {
    persistedUserData = JSON.parse(savedUserData);
  }
} catch (error) {
  console.error("Error loading persisted user data:", error);
}

// Token-based user (from JWT decode) - keep separate
const tokenUser = savedToken ? jwt_decode(savedToken.access_token) : null;

const slice = createSlice({
  name: "auth",
  initialState: {
    // Token-based user (from JWT decode) - primary source
    user: tokenUser,
    token: savedToken ? savedToken : null,
    subjectLoggedIn: tokenUser?.details?.[0]?.[0]?.['role'] == 'subject' || false,
    sponsorId: tokenUser?.details?.[0]?.[0]?.["sponsorId"] || persistedUserData?.sponsorId || null,
    isSystemAdmin: tokenUser?.details?.[0]?.[0]?.['role'] == 'system_admin' || false,
    userData: persistedUserData,
    isGetMeLoading: false,
    isGetMeInitialized: !!persistedUserData, // Track if we've loaded getMe at least once
  },
  reducers: {
    setSubjectLoggedIn: (state, action) => {
      state.subjectLoggedIn = true;
    },
    setGetMeLoading: (state, action) => {
      state.isGetMeLoading = action.payload;
    },
    clearUserData: (state) => {
      // Only clear getMe data, keep token-based user separate
      state.userData = null;
      state.isGetMeInitialized = false;
      localStorage.removeItem("userData");
    },
    clearAuth: (state) => {
      // Clear everything (token + getMe) on logout
      state.user = null;
      state.token = null;
      state.userData = null;
      state.subjectLoggedIn = false;
      state.sponsorId = null;
      state.isGetMeInitialized = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        bloqcibeApi.endpoints.login.matchFulfilled,
        (state, { payload }) => {
          if(payload.access_token){
            const jwtToken = jwt_decode(payload.access_token);
            state.token = payload;
            state.user = jwt_decode(payload.access_token);
            state.subjectLoggedIn = jwtToken.details?.[0]?.[0]?.['role'] == 'subject';
            // state.sponsorId = jwtToken.details?.[0]?.[0]?.["sponsorId"];
            localStorage.setItem("token", JSON.stringify(payload));
            // Clear persisted userData on new login - will be refreshed by getMe
            state.userData = null;
            state.isGetMeInitialized = false;
            localStorage.removeItem("userData");
          }
        },
      )
      .addMatcher(
        bloqcibeApi.endpoints.verifyAuthOTP.matchFulfilled,
        (state, { payload }) => {
          if(payload.access_token){
            const jwtToken = jwt_decode(payload.access_token);
            state.token = payload;
            state.user = jwt_decode(payload.access_token);
            state.isSystemAdmin = jwtToken.details?.[0]?.[0]?.['role'] == 'system_admin';
            state.subjectLoggedIn = jwtToken.details?.[0]?.[0]?.['role'] == 'subject';
            // state.sponsorId = jwtToken.details?.[0]?.[0]?.["sponsorId"];
            localStorage.setItem("token", JSON.stringify(payload));
            // Clear persisted userData on new login - will be refreshed by getMe
            state.userData = null;
            state.isGetMeInitialized = false;
            localStorage.removeItem("userData");
          }
        }
      )
      .addMatcher(
        bloqcibeApi.endpoints.getMeUser.matchPending,
        (state) => {
          state.isGetMeLoading = true;
        }
      )
      .addMatcher(
        bloqcibeApi.endpoints.getMeUser.matchFulfilled,
        (state, { payload }) => {
          if (payload) {
            // Store getMe data separately - don't modify token-based user
            state.userData = payload;
            state.isGetMeLoading = false;
            state.isGetMeInitialized = true;
            state.sponsorId = payload.sponsorId;
            
            // Persist to localStorage
            try {
              localStorage.setItem("userData", JSON.stringify(payload));
            } catch (error) {
              console.error("Error persisting user data:", error);
            }
          }
        }
      )
      .addMatcher(
        bloqcibeApi.endpoints.getMeUser.matchRejected,
        (state) => {
          state.isGetMeLoading = false;
        }
      );
  },
});

export default slice.reducer;
export const { setSubjectLoggedIn, setGetMeLoading, clearUserData, clearAuth } = slice.actions;

// Token-based selectors (primary)
export const selectCurrentUser = (state) => state.auth.user; // Token-based user
export const selectToken = (state) => state.auth.token;
export const selectSponsorId = (state) => {
  // Prioritize getMe sponsorId, fallback to token
  return state.auth.userData?.sponsorId || state.auth.sponsorId;
};
export const selectSubjectLoggedIn = (state) => {
  // Prioritize getMe data, fallback to token
  if (state.auth.userData?.userRoles) {
    return state.auth.userData.userRoles.some(role => role.role === 'subject');
  }
  return state.auth.subjectLoggedIn;
};

// getMe-based selectors (enhancement layer)
export const selectUserData = (state) => state.auth.userData; // getMe API response
export const selectUserRoles = (state) => state.auth.userData?.userRoles || []; // getMe userRoles
export const selectIsGetMeInitialized = (state) => state.auth.isGetMeInitialized;
export const selectIsGetMeLoading = (state) => state.auth.isGetMeLoading;
